import type { AutotaskTicketDetail } from "../services/api";

/**
 * In-portal Customer Thermometer CSAT survey.
 *
 * This renders the SAME four-button survey Autotask embeds in the resolution
 * email, so the ticket's requester can rate a Resolved/Complete ticket from
 * the portal without waiting for (or digging out) the email. It is display-only:
 * each button is an ordinary outbound link to Customer Thermometer, which logs
 * the rating and writes it back to the Autotask ticket UDF ("Customer Thermometer
 * Temperature") exactly as the email path does. The portal never captures the
 * rating itself.
 *
 * Ryan, 2026-09-25 (#189): only the ticket's requester sees these buttons.
 * Every other viewer (a client admin, a colleague with view_all_company_tickets,
 * staff, and staff using View as) gets no control and no rating links. An
 * existing read-only temperature on the ticket stays visible to anyone who can
 * open it.
 *
 * The button URLs are the email template's URLs verbatim: `template`, `hash`
 * and `embed_data` are static per button (embed_data decodes to
 * `temperature_id=1..4&thermometer_id=197870&nps_rating=`, i.e. the rating
 * value + our thermometer id, nothing recipient-specific), and every other
 * query param is an Autotask email merge field that Autotask fills at send
 * time. Here we fill them portal-side instead:
 *   e  [Contact: Email Address]        -> the ticket contact's email
 *   f  [Contact: First Name]           -> the ticket contact's first name
 *   l  [Contact: Last Name]            -> the ticket contact's last name
 *   c  [Account: Name]                 -> ticket company
 *   c1 [Ticket: ID]                    -> ticket id (CT maps the response back to
 *                                          the right Autotask ticket via this)
 *   c2 [Ticket: Number]                -> ticket number
 *   c3 [Account: Classification Name]  -> constant "Managed Service" (every
 *                                          portal client is a Managed Service client)
 *   c4 [Ticket: Completed By]          -> assigned technician (so a customer who
 *                                          closes their own ticket doesn't show as
 *                                          the completer)
 * e/f/l come from the ticket detail payload, and only when the server has
 * decided this viewer is the requester. They are never taken from /api/me.
 * If that contact data is missing, the control is not rendered.
 */

const CT_BASE = "https://app.customerthermometer.com/";
const CT_ICONS =
  "https://app.customerthermometer.com/sites/app/images/icon_sets/icon_set_8s";

interface RatingButton {
  hash: string;
  embed: string;
  label: string;
  color: string;
  img: string;
  alt: string;
}

// Lifted verbatim from the Autotask resolution-email template (thermometer id
// 197870). Order = best -> worst, matching the email row.
const RATINGS: RatingButton[] = [
  {
    hash: "5de6b9be",
    embed: "dGVtcGVyYXR1cmVfaWQ9MSZ0aGVybW9tZXRlcl9pZD0xOTc4NzAmbnBzX3JhdGluZz0=",
    label: "Fantastic",
    color: "#B17657",
    img: `${CT_ICONS}/gold.png`,
    alt: "Gold Star",
  },
  {
    hash: "559ca4a0",
    embed: "dGVtcGVyYXR1cmVfaWQ9MiZ0aGVybW9tZXRlcl9pZD0xOTc4NzAmbnBzX3JhdGluZz0=",
    label: "Good",
    color: "#66CC33",
    img: `${CT_ICONS}/green.png`,
    alt: "Green Light",
  },
  {
    hash: "62cd4162",
    embed: "dGVtcGVyYXR1cmVfaWQ9MyZ0aGVybW9tZXRlcl9pZD0xOTc4NzAmbnBzX3JhdGluZz0=",
    label: "Neutral",
    color: "#FFB40D",
    img: `${CT_ICONS}/amber.png`,
    alt: "Yellow Light",
  },
  {
    hash: "a529abbb",
    embed: "dGVtcGVyYXR1cmVfaWQ9NCZ0aGVybW9tZXRlcl9pZD0xOTc4NzAmbnBzX3JhdGluZz0=",
    label: "Review",
    color: "#eb0a0a",
    img: `${CT_ICONS}/red.png`,
    alt: "Red Light",
  },
];

interface SurveyFields {
  e: string;
  f: string;
  l: string;
  c: string;
  c1: string;
  c2: string;
  c3: string;
  c4: string;
}

// Static parts (template/hash/embed_data) are already URL-safe and are kept
// verbatim to match the email URLs byte-for-byte; only the substituted merge
// values are percent-encoded (as Autotask does at send time).
const VERBATIM = new Set(["template", "hash", "embed_data"]);

function buildSurveyUrl(btn: RatingButton, f: SurveyFields): string {
  const pairs: [string, string][] = [
    ["template", "log_feedback"],
    ["hash", btn.hash],
    ["embed_data", btn.embed],
    ["e", f.e],
    ["f", f.f],
    ["l", f.l],
    ["c", f.c],
    ["c1", f.c1],
    ["c2", f.c2],
    ["c3", f.c3],
    ["c4", f.c4],
    ["c5", ""],
    ["c6", ""],
    ["c7", ""],
    ["c8", ""],
    ["c9", ""],
    ["c10", ""],
  ];
  const query = pairs
    .map(([k, v]) => `${k}=${VERBATIM.has(k) ? v : encodeURIComponent(v)}`)
    .join("&");
  return `${CT_BASE}?${query}`;
}

/**
 * True when a ticket is in a state where the CSAT survey should be offered:
 * Complete (status id 5) or Resolved (the dedicated holding status id 21, once
 * that ships; also matched by label). Cancelled is deliberately excluded.
 * There is no service experience to rate.
 */
export function csatSurveyEligible(t: AutotaskTicketDetail): boolean {
  if (t.status_id === 5 || t.status_id === 21) return true;
  const label = (t.status_label ?? "").toLowerCase();
  return label.includes("complete") || label.includes("resolved");
}

/**
 * e/f/l for the rating link. Null unless this viewer is the requester and the
 * ticket contact's email, first name, and last name were all returned. A blank
 * name is allowed (the contact record had that field). A missing field is not:
 * we do not fill it from /api/me.
 */
function ticketContactFields(
  ticket: AutotaskTicketDetail,
): { e: string; f: string; l: string } | null {
  if (ticket.viewer_is_requester !== true) return null;
  const email = ticket.csat_contact_email;
  const first = ticket.csat_contact_first_name;
  const last = ticket.csat_contact_last_name;
  if (typeof email !== "string" || email.trim() === "") return null;
  if (typeof first !== "string" || typeof last !== "string") return null;
  return { e: email.trim(), f: first.trim(), l: last.trim() };
}

export function CsatSurvey({ ticket }: { ticket: AutotaskTicketDetail }) {
  if (!csatSurveyEligible(ticket)) return null;
  const contact = ticketContactFields(ticket);
  if (!contact) return null;

  const fields: SurveyFields = {
    e: contact.e,
    f: contact.f,
    l: contact.l,
    c: ticket.company_name ?? "",
    c1: ticket.id != null ? String(ticket.id) : "",
    c2: ticket.ticketNumber ?? "",
    c3: "Managed Service",
    c4: ticket.assignee_name ?? "",
  };

  return (
    <div className="csat-survey">
      <div className="csat-survey-title">
        How did you find the support you received today?
      </div>
      <div className="csat-survey-buttons">
        {RATINGS.map((btn) => (
          <a
            key={btn.hash}
            className="csat-survey-btn"
            href={buildSurveyUrl(btn, fields)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              className="csat-survey-icon"
              src={btn.img}
              alt={btn.alt}
              title={btn.alt}
            />
            <span className="csat-survey-label" style={{ color: btn.color }}>
              {btn.label}
            </span>
          </a>
        ))}
      </div>
      <div className="csat-survey-hint">
        Your feedback is valuable. Tap the rating which best represents your
        experience.
      </div>
    </div>
  );
}
